// Generic PDF export hook.
//
// Owns the state machine (idle → generating → ready → error), keeps a blob
// cache keyed by a stable filter hash so re-clicking Download doesn't
// regenerate, and exposes Download / Open / Share actions consistent with
// `deliverPdf` + the in-app PdfViewerContext.

import { useCallback, useRef, useState } from "react";
import type jsPDF from "jspdf";
import { toast } from "sonner";
import { isDespiaNative } from "@/lib/isDespiaNative";
import { getPdfViewerHandle } from "@/contexts/PdfViewerContext";
import { openPdfInNativeViewer } from "@/lib/pdfDelivery";

export type ExportState = "idle" | "generating" | "ready" | "error";

export interface GeneratedPdf {
  blob: Blob;
  filename: string;
  /** Tracked so we revoke object URLs when the consumer unmounts. */
  cacheKey: string;
}

interface UseExportPdfReturn {
  state: ExportState;
  error: string | null;
  result: GeneratedPdf | null;
  /** Run a generator function. Caches by stable cacheKey. */
  generate: (
    cacheKey: string,
    filename: string,
    builder: () => Promise<jsPDF> | jsPDF,
  ) => Promise<GeneratedPdf | null>;
  download: () => void;
  open: () => void;
  share: () => Promise<void>;
  /** True if Web Share with files is supported. */
  canShare: boolean;
  reset: () => void;
}

export function useExportPdf(): UseExportPdfReturn {
  const [state, setState] = useState<ExportState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratedPdf | null>(null);
  const cacheRef = useRef<Map<string, GeneratedPdf>>(new Map());

  const generate = useCallback<UseExportPdfReturn["generate"]>(
    async (cacheKey, filename, builder) => {
      const cached = cacheRef.current.get(cacheKey);
      if (cached) {
        setResult(cached);
        setState("ready");
        setError(null);
        return cached;
      }
      setState("generating");
      setError(null);
      try {
        const doc = await builder();
        const blob = doc.output("blob");
        const res: GeneratedPdf = { blob, filename, cacheKey };
        cacheRef.current.set(cacheKey, res);
        setResult(res);
        setState("ready");
        return res;
      } catch (e: any) {
        console.error("[useExportPdf] generation failed:", e);
        setError(e?.message ?? "PDF generation failed");
        setState("error");
        return null;
      }
    },
    [],
  );

  const download = useCallback(() => {
    if (!result) return;
    const url = URL.createObjectURL(result.blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = result.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1500);
  }, [result]);

  const open = useCallback(async () => {
    if (!result) return;
    // Native: open in the OS QuickLook viewer (flawless pinch / share / save).
    if (isDespiaNative() && (await openPdfInNativeViewer(result.blob, result.filename))) {
      return;
    }
    const viewer = getPdfViewerHandle();
    const file = new File([result.blob], result.filename, { type: "application/pdf" });
    const url = URL.createObjectURL(file);
    if (viewer) {
      viewer.open({ url, filename: result.filename, blob: file });
      return;
    }
    // Fallback: open in new tab / current location
    try {
      window.open(url, "_blank");
    } catch {
      window.location.href = url;
    }
  }, [result]);

  const canShare = (() => {
    if (typeof navigator === "undefined") return false;
    if (!("share" in navigator)) return false;
    // canShare with files requires a File; check API existence.
    return typeof (navigator as any).canShare === "function";
  })();

  const share = useCallback(async () => {
    if (!result) return;
    const file = new File([result.blob], result.filename, { type: "application/pdf" });

    // Native share sheet works via the Web Share API (it has a user gesture
    // here). The old despia(`file://`) call wasn't a real command and silently
    // no-op'd, so it's removed.
    try {
      const nav = navigator as any;
      if (nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: result.filename });
        return;
      }
      if ("share" in navigator) {
        await navigator.share({ title: result.filename });
        return;
      }
    } catch (e: any) {
      // User cancellation throws — silent
      if (e?.name === "AbortError") return;
      console.warn("[useExportPdf] share failed, falling back:", e);
    }

    // Final fallback — open it (native QuickLook on native, in-app viewer else).
    await open();
  }, [result, open]);

  const reset = useCallback(() => {
    setState("idle");
    setError(null);
    setResult(null);
  }, []);

  return { state, error, result, generate, download, open, share, canShare, reset };
}

/**
 * Filename helper — matches the canonical Ulture Pest pattern. Con marca, `prefijo`
 * es el nombre de la empresa (Personalizar informes, F5: `prefijoFichero`).
 */
export function buildExportFilename(reportId: string, from?: string, to?: string, prefijo = "ulture-pest"): string {
  const parts = [prefijo, reportId];
  if (from && to) parts.push(from, "to", to);
  return parts.join("-") + ".pdf";
}
